// Pure helpers for the seasonality matrix. No React, no Supabase — everything
// here is a function of its arguments, so the sorting rules can be reasoned
// about (and tested) on their own.

export const MONTH_INITIALS = ['J', 'F', 'M', 'A', 'M', 'J', 'J', 'A', 'S', 'O', 'N', 'D']
export const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
]

export const REQUIRES_ORDER = ['walk', 'hike', 'winter_kit', 'guide', 'technical']
export const REQUIRES_LABELS = {
  walk: 'Walk',
  hike: 'Hike',
  winter_kit: 'Winter kit',
  guide: 'Guide',
  technical: 'Technical',
}
// The default filter. Ranking by feasibility alone floats the drive-ups to the
// top of every month, so `walk` is excluded until you ask for it.
export const HIKE_AND_ABOVE = ['hike', 'winter_kit', 'guide', 'technical']

// Postgres hands smallint[] back as an array; a hand-edited row might hold
// strings or the wrong length. Anything that isn't twelve usable numbers is
// treated as "no data" rather than silently half-rendered.
export function normaliseScores(value) {
  if (!Array.isArray(value) || value.length !== 12) return null
  const scores = value.map((entry) => {
    const number = Number(entry)
    if (!Number.isFinite(number)) return null
    return Math.min(100, Math.max(0, Math.round(number)))
  })
  return scores.every((entry) => entry !== null) ? scores : null
}

export function feasibleMonthCount(scores, threshold) {
  if (!scores) return 0
  return scores.reduce((count, score) => (score >= threshold ? count + 1 : count), 0)
}

export function isFeasibleIn(scores, monthIndex, threshold) {
  if (!scores || monthIndex === null || monthIndex === undefined) return false
  return scores[monthIndex] >= threshold
}

/**
 * Scarcity sort. With a month selected, the question is not "what scores
 * highest in March" — that is always the year-round drive-ups — but "what is
 * March actually for". So peaks that clear the threshold in the selected month
 * are ordered by how FEW months they clear overall: a peak feasible only in
 * March ranks above one feasible in March and April, and a peak feasible all
 * year sinks to the bottom. Ties break on the selected month's own score.
 * Peaks that miss the threshold in that month keep their incoming order and
 * are flagged so the view can dim them.
 *
 * Returns a new array of { row, scores, feasibleMonths, monthScore, belowThreshold }.
 */
export function sortByScarcity(rows, monthIndex, threshold) {
  const decorated = rows.map((row, index) => {
    const scores = normaliseScores(row.season_scores)
    return {
      row,
      index,
      scores,
      feasibleMonths: feasibleMonthCount(scores, threshold),
      monthScore: scores && monthIndex !== null ? scores[monthIndex] : null,
      belowThreshold: monthIndex === null ? false : !isFeasibleIn(scores, monthIndex, threshold),
    }
  })

  if (monthIndex === null || monthIndex === undefined) return decorated

  const eligible = decorated.filter((entry) => !entry.belowThreshold)
  const rest = decorated.filter((entry) => entry.belowThreshold)

  eligible.sort((a, b) => {
    if (a.feasibleMonths !== b.feasibleMonths) return a.feasibleMonths - b.feasibleMonths
    if (a.monthScore !== b.monthScore) return b.monthScore - a.monthScore
    return a.index - b.index
  })

  return [...eligible, ...rest]
}

// Sequential single-hue ramp. Sequential rather than categorical because the
// scale is one ordered quantity; a rainbow would imply categories that aren't
// there. Below the threshold the cell is left almost blank so the eye skips it.
export function scoreTint(score, threshold = 0) {
  if (score === null || score === undefined) return 'transparent'
  if (score < threshold) return 'rgba(120, 140, 160, .07)'
  const strength = 0.06 + (score / 100) * 0.82
  return `rgba(37, 99, 165, ${strength.toFixed(3)})`
}

export function scoreTextColor(score, threshold = 0) {
  if (score === null || score === undefined) return 'inherit'
  if (score < threshold) return 'rgba(130, 145, 155, .75)'
  return score >= 62 ? '#f2f6fb' : '#1d2a33'
}

// Points for the polyline drawn across a row, in a 0..12 by 0..100 space that
// the SVG stretches to the cells. Index i sits at the centre of cell i.
export function polylinePoints(scores) {
  if (!scores) return ''
  return scores.map((score, index) => `${index + 0.5},${100 - score}`).join(' ')
}

export function areaPath(scores) {
  if (!scores) return ''
  const line = scores.map((score, index) => `${index === 0 ? 'M' : 'L'}${index + 0.5},${100 - score}`).join(' ')
  return `${line} L11.5,100 L0.5,100 Z`
}

export function requiresFilterMatches(activeRequires, value) {
  if (!activeRequires.length) return true
  return activeRequires.includes(String(value || 'hike'))
}
